import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useDevelopmentDemo } from '../../contexts/DevelopmentDemoContext';
import type { DevReview, Submission } from '../../types/developmentDemo';
import { DemoSectionHeader } from '../demoShell/DemoSectionHeader';
import { glassPanel, linkButton } from '../../utils/demoUi';
import { reviewForSubmission, submissionsFor } from '../../utils/developmentDemo';
import { LevelBadge } from './DevBadges';
import { DecisionForm } from './DecisionForm';
type Column = {
  id: string;
  name: string;
  submission?: Submission;
  review?: DevReview;
};
export function CandidateComparison() {
  const {
    state,
    config,
    dispatch
  } = useDevelopmentDemo();
  const columns: Column[] = config.participants.map(participant => {
    // Latest reviewed submission for this candidate.
    const reviewedSubmission = [...submissionsFor(state, participant.id)].reverse().find(submission => reviewForSubmission(state, submission.id)?.outcome === 'reviewed');
    return {
      id: participant.id,
      name: participant.name,
      submission: reviewedSubmission,
      review: reviewedSubmission ? reviewForSubmission(state, reviewedSubmission.id) : undefined
    };
  });
  const cell = (content: React.ReactNode) => <td className="px-5 py-4 align-top leading-relaxed text-ink-dark" data-id="element-520" data-mp="2rcwgij25chyj">{content}</td>;
  const empty = <span className="text-ink-muted" data-id="element-521" data-mp="2mfe6sqgb1yph">No reviewed work sample yet</span>;
  return <div data-id="element-522" data-mp="3j2ivauhctef6">
      <DemoSectionHeader title="Candidate Comparison" description={`${config.organization}. The same role criteria for each candidate, with original responses and reviewer explanations. Candidates are not ranked automatically.`} data-id="element-523" data-mp="3j3amlt3x4pzn" />

      <div className={`${glassPanel} overflow-x-auto`} data-id="element-524" data-mp="35cxhrzmbprej">
        <table className="w-full min-w-[760px] text-left text-sm" data-id="element-525" data-mp="35abbs4f2zjyf">
          <caption className="sr-only" data-id="element-526" data-mp="6n740cox1890">Candidate evidence compared on the same role criteria</caption>
          <thead data-id="element-527" data-mp="1ux6aahk73w6">
            <tr className="border-b border-line" data-id="element-528" data-mp="1udy3lhpntvc">
              <th scope="col" className="w-48 px-5 py-4 text-xs font-semibold text-ink-muted" data-id="element-529" data-mp="krfk8vhmxp73">
                Role criterion
              </th>
              {columns.map(column => <th key={column.id} scope="col" className="px-5 py-4 align-top" data-id="element-530" data-mp="bzvo8h4dttft">
                  <span className="block font-heading text-base font-bold text-ink-dark" data-id="element-531" data-mp="bxsze0eyu6co">{column.name}</span>
                  <span className="block text-xs font-normal text-ink-muted" data-id="element-532" data-mp="21fyoil06s8f">
                    {column.submission ? `${column.submission.id} · ${column.review?.id}` : 'Awaiting reviewed evidence'}
                  </span>
                  <button type="button" onClick={() => dispatch({
                type: 'selectParticipant',
                id: column.id,
                section: 'records'
              })} className={`mt-2 ${linkButton}`} data-id="element-533" data-mp="3p6xgvzrfh4dq">
                    Inspect record
                    <ArrowRight size={14} aria-hidden="true" data-id="element-534" data-mp="1rg47yayaru28" />
                    <span className="sr-only" data-id="element-535" data-mp="1rfkzrlyg8k1e"> for {column.name}</span>
                  </button>
                </th>)}
            </tr>
          </thead>
          <tbody data-id="element-536" data-mp="37xuud134siib">
            {config.criteria.map(criterion => <tr key={criterion.id} className="border-b border-line" data-id="element-537" data-mp="3lnye9lejg04l">
                <th scope="row" className="px-5 py-4 align-top font-semibold text-ink-dark" data-id="element-538" data-mp="3lq2pkoero1ew">
                  {criterion.label}
                  <span className="mt-0.5 block text-xs font-normal text-ink-muted" data-id="element-539" data-mp="3vlwaow6jx38a">{criterion.description}</span>
                </th>
                {columns.map(column => {
              const observation = column.review?.observations.find(entry => entry.criterionId === criterion.id);
              return <React.Fragment key={column.id} data-id="element-540" data-mp="3joa2l0unjee">
                      {cell(observation ? <>
                            <LevelBadge level={observation.level} data-id="element-541" data-mp="259zuyg3tpugl" />
                            <p className="mt-1 text-ink-muted" data-id="element-542" data-mp="25bt01qysg6mh">{observation.observation}</p>
                          </> : empty)}
                    </React.Fragment>;
            })}
              </tr>)}
            <tr className="border-b border-line" data-id="element-543" data-mp="2lo5zrt27lbv3">
              <th scope="row" className="px-5 py-4 align-top font-semibold text-ink-dark" data-id="element-544" data-mp="2lnw8811imwna">
                Original response
              </th>
              {columns.map(column => <React.Fragment key={column.id} data-id="element-545" data-mp="1t0ijgq7z17e9">{cell(column.submission ? <span className="whitespace-pre-wrap" data-id="element-546" data-mp="1xytq6s2yz1s9">{column.submission.response}</span> : empty)}</React.Fragment>)}
            </tr>
            <tr className="border-b border-line" data-id="element-547" data-mp="22qbr5g4zt2d8">
              <th scope="row" className="px-5 py-4 align-top font-semibold text-ink-dark" data-id="element-548" data-mp="15aftqgrj4dit">
                Reviewer explanation
              </th>
              {columns.map(column => <React.Fragment key={column.id} data-id="element-549" data-mp="12pq72q5fxun6">{cell(column.review?.feedback ?? empty)}</React.Fragment>)}
            </tr>
            <tr data-id="element-550" data-mp="xqdi9zv0edhy">
              <th scope="row" className="px-5 py-4 align-top font-semibold text-ink-dark" data-id="element-551" data-mp="xrydlrp5e0gv">
                Unanswered questions
              </th>
              {columns.map(column => <React.Fragment key={column.id} data-id="element-552" data-mp="nrfrsre0w9wb">{cell(column.review ? column.review.openQuestions || 'None recorded' : empty)}</React.Fragment>)}
            </tr>
          </tbody>
        </table>
      </div>

      <section aria-labelledby="decisions-title" className="mt-10" data-id="element-553" data-mp="eykpo6juquar">
        <h2 id="decisions-title" className="font-heading text-lg font-bold text-ink-dark" data-id="element-554" data-mp="a09ftvtk4jsj">
          Record human decisions
        </h2>
        <p className="mt-1 max-w-3xl text-sm leading-relaxed text-ink-muted" data-id="element-555" data-mp="53su189tyhim">{config.note}</p>
        <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2" data-id="element-556" data-mp="23h1n31rtrw">
          {columns.map(column => <DecisionForm key={column.id} participantId={column.id} data-id="element-557" data-mp="3s8s876ncpb5g" />)}
        </div>
      </section>
    </div>;
}